import { getPlatformType } from './platformUtils';

// Device ID storage key
const DEVICE_ID_KEY = 'ordease_device_id';

/**
 * Generate a cryptographically secure random UUID
 * @returns {string} A new UUID
 */
export const generateUUID = () => {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Enhanced fallback for older browsers with better entropy
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  const platformPart = getPlatformType().substring(0, 3);
  
  // Combine multiple sources of randomness
  const additionalRandom = Math.random().toString(36).substring(2, 8);
  const performanceNow = (typeof performance !== 'undefined' && performance.now) 
    ? performance.now().toString(36) 
    : Math.random().toString(36).substring(2, 8);
  
  return `${platformPart}_${timestamp}_${randomPart}_${additionalRandom}_${performanceNow}`;
};

/**
 * Get the current device ID, generating one if it doesn't exist
 * @returns {string} The device ID
 */
export const getDeviceId = () => {
  let deviceId = getStoredDeviceId();
  
  if (!deviceId) {
    deviceId = generateUUID();
    storeDeviceId(deviceId);
  }
  
  return deviceId;
};

/**
 * Get stored device ID from platform-specific storage
 * @returns {string|null} The stored device ID or null if not found
 */
export const getStoredDeviceId = () => {
  try {
    const platformType = getPlatformType();
    
    if (platformType === 'ios' || platformType === 'android') {
      // For native platforms, use localStorage as fallback
      // In a production app, you might want to use:
      // iOS: Keychain, Android: EncryptedSharedPreferences
      return localStorage.getItem(DEVICE_ID_KEY);
    } else {
      // Web platforms
      return localStorage.getItem(DEVICE_ID_KEY);
    }
  } catch (error) {
    return null;
  }
};

/**
 * Store device ID in platform-specific storage
 * @param {string} deviceId - The device ID to store
 */
export const storeDeviceId = (deviceId) => {
  try {
    const platformType = getPlatformType();
    
    if (platformType === 'ios' || platformType === 'android') {
      // For native platforms, use localStorage as fallback
      // In a production app, you might want to use:
      // iOS: Keychain, Android: EncryptedSharedPreferences
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    } else {
      // Web platforms
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    
  } catch (error) {
    // Error storing device ID - handled silently
  }
};

/**
 * Get minimal device information needed for the UUID system
 * @returns {Object} Device information object
 */
export const getDeviceInfo = () => {
  return {
    deviceId: getDeviceId(),
    platform: getPlatformType(),
    appVersion: getAppVersion()
  };
};

/**
 * Get app version information
 * @returns {string} App version
 */
export const getAppVersion = () => {
  // For web/PWA, you might want to get this from package.json or environment
  // For native apps, this would come from Capacitor
  return process.env.REACT_APP_VERSION || '1.0.1';
};

/**
 * Check if device ID is valid
 * @param {string} deviceId - The device ID to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidDeviceId = (deviceId) => {
  if (!deviceId || typeof deviceId !== 'string') {
    return false;
  }
  
  // Basic UUID format validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(deviceId);
};

/**
 * Clear stored device ID (useful for testing or user logout)
 */
export const clearDeviceId = () => {
  try {
    const platformType = getPlatformType();
    
    if (platformType === 'ios' || platformType === 'android') {
      localStorage.removeItem(DEVICE_ID_KEY);
    } else {
      localStorage.removeItem(DEVICE_ID_KEY);
    }
    
  } catch (error) {
    // Error clearing device ID - handled silently
  }
};

/**
 * Get device storage info for debugging
 * @returns {Object} Storage information
 */
export const getDeviceStorageInfo = () => {
  try {
    const platformType = getPlatformType();
    const deviceId = getStoredDeviceId();
    
    return {
      platformType,
      hasDeviceId: !!deviceId,
      deviceIdLength: deviceId ? deviceId.length : 0,
      isValidDeviceId: deviceId ? isValidDeviceId(deviceId) : false,
      storageAvailable: typeof Storage !== 'undefined',
      localStorageAvailable: typeof localStorage !== 'undefined'
    };
  } catch (error) {
    return {
      error: error.message
    };
  }
}; 